import type {
  CaseFolderTreeResponse,
  TableProcessingResponse,
  TabularExtractionMode,
} from '@/services/api/cases'
import type { DocumentMapping } from '@/services/api/structuredIngest'
import { isTableFileType } from '@/utils/table-files'

export const tableProcessingKey = (caseId: string, documentId: string) =>
  ['case', caseId, 'table-processing', documentId] as const

export function isTableFilename(filename: string): boolean {
  return isTableFileType(filename.split('.').pop())
}

/** A mixed upload sends the table choice only for table files. */
export function appendTableProcessingMode(
  form: FormData,
  filename: string,
  mode: TabularExtractionMode,
  override?: TabularExtractionMode
): void {
  if (isTableFilename(filename)) form.append('tabular_extraction_mode', override ?? mode)
}

interface StagedTableFile {
  id: string
  file: { name: string }
  status: string
  tableMode?: TabularExtractionMode
}

/** An override belongs to the staged file ID, even when several folders share a basename. */
export function updateStagedTableMode<T extends StagedTableFile>(
  files: T[],
  id: string,
  mode: TabularExtractionMode | undefined
): T[] {
  return files.map(file =>
    file.id === id &&
    isTableFilename(file.file.name) &&
    file.status !== 'uploading' &&
    file.status !== 'uploaded'
      ? { ...file, tableMode: mode }
      : file
  )
}

/** AI documents remain extractable even when an earlier structured mapping exists. */
export function selectedStructuredMappings(
  documents: readonly { id: string; tabular_extraction_mode?: TabularExtractionMode }[],
  mappings: Record<string, DocumentMapping> = {}
): (DocumentMapping & { id: string })[] {
  return documents.flatMap(document => {
    const mapping = mappings[document.id]
    return mapping && document.tabular_extraction_mode !== 'llm'
      ? [{ id: document.id, ...mapping }]
      : []
  })
}

/** Apply the saved choice to the list's imperative snapshot without waiting for a reload. */
export function applyTableProcessing(
  tree: CaseFolderTreeResponse | null,
  update: TableProcessingResponse
): CaseFolderTreeResponse | null {
  if (!tree?.files.some(file => file.document.id === update.document_id)) return tree
  return {
    ...tree,
    files: tree.files.map(file =>
      file.document.id === update.document_id
        ? {
            ...file,
            document: { ...file.document, tabular_extraction_mode: update.tabular_extraction_mode },
          }
        : file
    ),
  }
}

export function tableProcessingUpdate(
  caseId: string,
  key: readonly unknown[],
  data: unknown
): TableProcessingResponse | null {
  if (key[0] !== 'case' || key[1] !== caseId || key[2] !== 'table-processing') return null
  if (
    !data ||
    typeof data !== 'object' ||
    !('document_id' in data) ||
    !('tabular_extraction_mode' in data)
  )
    return null
  if (typeof data.document_id !== 'string' || data.document_id !== key[3]) return null
  const mode = data.tabular_extraction_mode
  return mode === 'structured' || mode === 'llm'
    ? { document_id: data.document_id, tabular_extraction_mode: mode }
    : null
}
